/**
 * Resolve follow-up prompt chips for an assistant chat message.
 * Prefers edge-provided metadata; falls back to chart-derived prompts so chips
 * still appear when the edge function has not yet attached suggestedPrompts.
 */
import type { ChatMessageRow } from '../types/ai-chat.types';

const GENERIC_CATEGORY_PROMPTS = [
  'Break it down further by month',
  'Look at specific merchants',
  'Compare to another period',
] as const;

export function resolveFollowUpPrompts(msg: ChatMessageRow): string[] {
  const fromMeta = msg.metadata?.suggestedPrompts;
  if (Array.isArray(fromMeta) && fromMeta.length > 0) {
    return fromMeta.filter((p): p is string => typeof p === 'string' && p.trim().length > 0);
  }

  const chart = msg.metadata?.charts?.[0];
  if (chart?.type === 'category_spend_bar') {
    const title = chart.title ?? '';
    // Titles look like "Dining & Hospitality (2025-09-14 to 2026-09-13)" or
    // "Spending by category (2025-09-01 to 2026-08-31)".
    if (title && !title.startsWith('Spending by category')) {
      const paren = title.indexOf(' (');
      const filter = (paren > 0 ? title.slice(0, paren) : title).trim();
      if (filter) {
        return [
          `Break down ${filter} by month`,
          `Look at specific merchants in ${filter}`,
          `Compare ${filter} to another period`,
        ];
      }
    }
    return [...GENERIC_CATEGORY_PROMPTS];
  }

  // Soft fallback when the assistant invites a follow-up but no chart/metadata.
  if (
    msg.role === 'assistant' &&
    /would you like/i.test(msg.content) &&
    /break|compare|merchant|month|further/i.test(msg.content)
  ) {
    return [...GENERIC_CATEGORY_PROMPTS];
  }

  return [];
}
